/**
 * DTemplate Frame
 * The draft deck's two slide layouts and the blocks its slides are built
 * from, on the slide canvas
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion, type TargetAndTransition } from 'framer-motion';
import { EASE_OUT_EXPO, FREEZE_FOR_CAPTURE } from '@components/motion/scroll-motion';
import { SlideCanvas } from '@components/press/pitch-deck/slide-canvas';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-10-05: a new approach to the deck on the template of
 * Distributional's seed deck — for each of its slides, the question it
 * answers and The Home CEO's answer to it. Its two layouts, in this
 * deck's style:
 *
 * - StatementSlide, its title-band slides (Problem, Market, Team, …):
 *   the section, the question, the answer as a headline, then the content.
 * - SplitSlide, its panel slides (GTM, Flywheel, the examples, …): the
 *   answer and its points in a deep seafoam panel, a picture beside it.
 *
 * Every slide wears the question it answers in a coral chip, so the draft
 * reads as answers to an investor's questions; `SHOW_QUESTIONS` takes them
 * all off.
 *
 * Blocks arrive in turn (`useArrive`); still — reduced motion, or
 * `?capture=1`, how decks are exported — they stand in place.
 *
 * Every list here is a slide's fixed content, never reordered or edited
 * in place, so an item's index is a stable key.
 */

/** The questions on the slides, while the deck is a draft. */
export const SHOW_QUESTIONS = true;

// ── Arrival ─────────────────────────────────────────────────────────────────

const StillContext = createContext(false);

/** Whether the slide stands still (reduced motion, or the export), for a
 *  picture that moves after it arrives. */
export function useStill() {
  return useContext(StillContext);
}

/** Motion props for the i-th thing to arrive on a slide. */
export function useArrive() {
  const still = useContext(StillContext);
  return (i: number, initial: TargetAndTransition = { opacity: 0, y: 16 }) => ({
    initial: still ? (false as const) : initial,
    animate: { opacity: 1, y: 0, x: 0, scale: 1 },
    transition: { duration: 0.65, ease: EASE_OUT_EXPO, delay: 0.2 + i * 0.12 },
  });
}

const StillCanvas: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  return (
    <SlideCanvas>
      <StillContext.Provider value={still}>{children}</StillContext.Provider>
    </SlideCanvas>
  );
};

// ── Layouts ─────────────────────────────────────────────────────────────────

type LayoutProps = {
  /** The template slide's section, e.g. "Problem". */
  section: string;
  /** The question the template slide answers. */
  question: string;
  /** The Home CEO's answer, in a line. */
  headline: React.ReactNode;
  children?: React.ReactNode;
};

/** The section and the answer across the top, the content below. A `long`
 *  headline (three lines) sets smaller, so it clears the content. */
export const StatementSlide: React.FC<LayoutProps & { long?: boolean }> = ({
  section,
  question,
  headline,
  long = false,
  children,
}) => {
  const arrive = useArriveOutside();
  return (
    <StillCanvas>
      <Top {...arrive(0)}>
        <Eyebrow>{section}</Eyebrow>
        {SHOW_QUESTIONS && <Question>Q: {question}</Question>}
        <Headline $long={long}>{headline}</Headline>
      </Top>
      <Body>{children}</Body>
    </StillCanvas>
  );
};

/** The answer and its points in a panel on the left, a picture on the right. */
export const SplitSlide: React.FC<LayoutProps & { points?: readonly React.ReactNode[]; picture: React.ReactNode }> = ({
  section,
  question,
  headline,
  points = [],
  picture,
}) => (
  <StillCanvas>
    <SplitInner section={section} question={question} headline={headline} points={points} picture={picture} />
  </StillCanvas>
);

const SplitInner: React.FC<LayoutProps & { points: readonly React.ReactNode[]; picture: React.ReactNode }> = ({
  section,
  question,
  headline,
  points,
  picture,
}) => {
  const arrive = useArrive();
  return (
    <>
      <Panel>
        <motion.div {...arrive(0)}>
          <PanelEyebrow>{section}</PanelEyebrow>
          {SHOW_QUESTIONS && <PanelQuestion>Q: {question}</PanelQuestion>}
          <PanelHeadline>{headline}</PanelHeadline>
        </motion.div>
        <PanelPoints>
          {points.map((point, i) => (
            <motion.li key={i} {...arrive(i + 1)}>
              {point}
            </motion.li>
          ))}
        </PanelPoints>
      </Panel>
      <Picture>{picture}</Picture>
    </>
  );
};

/** For the header outside StillCanvas's provider: reads stillness itself. */
function useArriveOutside() {
  const still = (useReducedMotion() ?? false) || FREEZE_FOR_CAPTURE;
  return (i: number) => ({
    initial: still ? (false as const) : { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.7, ease: EASE_OUT_EXPO, delay: i * 0.12 },
  });
}

/** A divider or a cover-like slide: one big line, a smaller one under it. */
export const TitleSlide: React.FC<{ title: React.ReactNode; subtitle?: React.ReactNode; note?: React.ReactNode }> = ({
  title,
  subtitle,
  note,
}) => {
  const arrive = useArriveOutside();
  return (
    <StillCanvas>
      <TitleBlock {...arrive(0)}>
        <BigTitle>{title}</BigTitle>
        {subtitle && <BigSubtitle>{subtitle}</BigSubtitle>}
        {note && <TitleNote>{note}</TitleNote>}
      </TitleBlock>
    </StillCanvas>
  );
};

// ── Blocks ──────────────────────────────────────────────────────────────────

/** Cards across, each a title (and a figure, if it has one) over its words. */
export const CardRow: React.FC<{
  cards: ReadonlyArray<{ title: React.ReactNode; text: React.ReactNode; figure?: React.ReactNode; tone?: 'focus' }>;
  columns?: number;
  /** Column widths, when the cards shouldn't share the row evenly. */
  widths?: string;
  /** Smaller, for cards stacked beside a picture. */
  compact?: boolean;
}> = ({ cards, columns = cards.length, widths, compact = false }) => {
  const arrive = useArrive();
  return (
    <Cards style={{ gridTemplateColumns: widths ?? `repeat(${columns}, 1fr)`, gap: compact ? 18 : undefined }}>
      {cards.map((card, i) => (
        <Card key={i} $focus={card.tone === 'focus'} $compact={compact} {...arrive(i + 1)}>
          {card.figure && <CardFigure $compact={compact}>{card.figure}</CardFigure>}
          <CardTitle $compact={compact}>{card.title}</CardTitle>
          <CardText $compact={compact}>{card.text}</CardText>
        </Card>
      ))}
    </Cards>
  );
};

/** Points with a bold lead each. */
export const Points: React.FC<{ items: ReadonlyArray<{ lead?: React.ReactNode; text: React.ReactNode }> }> = ({ items }) => {
  const arrive = useArrive();
  return (
    <PointList>
      {items.map((item, i) => (
        <motion.li key={i} {...arrive(i + 1)}>
          {item.lead && <strong>{item.lead} </strong>}
          {item.text}
        </motion.li>
      ))}
    </PointList>
  );
};

/** A table: a header row, then rows; the first column the row's name, and
 *  `focus` marks the row (or column, `focusColumn`) to read first. */
export const Table: React.FC<{
  head: readonly React.ReactNode[];
  rows: ReadonlyArray<readonly React.ReactNode[]>;
  widths?: string;
  focusRow?: number;
  focusColumn?: number;
}> = ({ head, rows, widths, focusRow, focusColumn }) => {
  const arrive = useArrive();
  const template = widths ?? `repeat(${head.length}, 1fr)`;
  return (
    <TableBox {...arrive(1)}>
      <TableRow style={{ gridTemplateColumns: template }} $head>
        {head.map((cell, c) => (
          <TableCell key={c} $head $focus={c === focusColumn}>
            {cell}
          </TableCell>
        ))}
      </TableRow>
      {rows.map((row, r) => (
        <TableRow key={r} style={{ gridTemplateColumns: template }} $focus={r === focusRow}>
          {row.map((cell, c) => (
            <TableCell key={c} $name={c === 0} $focus={c === focusColumn}>
              {cell}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBox>
  );
};

/** Phases along a line, each its when, its name and what happens. */
export const Phases: React.FC<{
  phases: ReadonlyArray<{
    when: React.ReactNode;
    name: React.ReactNode;
    items: readonly React.ReactNode[];
    tone?: 'now';
    /** A point to call out under the phase's items, boxed. */
    note?: React.ReactNode;
  }>;
  /** Column widths, when a phase needs more room than the rest. */
  widths?: string;
}> = ({ phases, widths }) => {
  const arrive = useArrive();
  return (
    <PhaseRow style={{ gridTemplateColumns: widths ?? `repeat(${phases.length}, 1fr)` }}>
      <PhaseLine aria-hidden="true" />
      {phases.map((phase, i) => (
        <Phase key={i} {...arrive(i + 1)}>
          <PhaseDot $now={phase.tone === 'now'} aria-hidden="true" />
          <PhaseWhen $now={phase.tone === 'now'}>{phase.when}</PhaseWhen>
          <PhaseName>{phase.name}</PhaseName>
          <PhaseItems>
            {phase.items.map((item, k) => (
              <li key={k}>{item}</li>
            ))}
          </PhaseItems>
          {phase.note && <PhaseNote>{phase.note}</PhaseNote>}
        </Phase>
      ))}
    </PhaseRow>
  );
};

/** A loop: steps around a circle, each turning the next. */
export const Loop: React.FC<{ steps: readonly string[]; centre: React.ReactNode; size?: number }> = ({
  steps,
  centre,
  size = 780,
}) => {
  const arrive = useArrive();
  const r = size / 2 - 70;
  const c = size / 2;
  const at = (i: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / steps.length;
    return { x: c + r * Math.cos(a), y: c + r * Math.sin(a) };
  };
  return (
    <LoopBox style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle cx={c} cy={c} r={r} fill="none" stroke={colors.brand.extraDark} strokeWidth="3" strokeDasharray="10 10" />
        {steps.map((_, i) => {
          const a = -Math.PI / 2 + ((i + 0.5) * 2 * Math.PI) / steps.length;
          const x = c + r * Math.cos(a);
          const y = c + r * Math.sin(a);
          const deg = (a * 180) / Math.PI + 90;
          return (
            <path key={i} d="M -9 -8 L 9 0 L -9 8 Z" fill={colors.accent.coral.extraDark} transform={`translate(${x} ${y}) rotate(${deg})`} />
          );
        })}
      </svg>
      <LoopCentre>{centre}</LoopCentre>
      {steps.map((step, i) => {
        const p = at(i);
        return (
          <LoopStep key={step} style={{ left: p.x, top: p.y }} {...arrive(i + 1, { opacity: 0, scale: 0.85 })}>
            {step}
          </LoopStep>
        );
      })}
    </LoopBox>
  );
};

type MarkKind = 'yes' | 'part' | 'no';

const MARK_SAYS: Record<MarkKind, string> = { yes: 'Yes', part: 'Partly', no: 'No' };

/** A table cell's verdict — yes, partly or no — and why, in a word or two.
 *  A call, not an element, so a row of them reads as plain data. */
export function mark(is: MarkKind, why?: React.ReactNode): React.ReactElement {
  return (
    <MarkLine $is={is}>
      <svg width="30" height="30" viewBox="0 0 30 30" role="img" aria-label={MARK_SAYS[is]}>
        {is === 'yes' && (
          <>
            <circle cx="15" cy="15" r="13" fill={colors.brand.exxtraDark} />
            <path d="M9 15.5l4 4 8-8.5" fill="none" stroke={colors.white} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          </>
        )}
        {is === 'part' && (
          <>
            <circle cx="15" cy="15" r="12" fill="none" stroke={colors.neutral.mediumLight} strokeWidth="2.5" />
            <path d="M15 3a12 12 0 0 0 0 24z" fill={colors.neutral.mediumLight} />
          </>
        )}
        {is === 'no' && (
          <>
            <circle cx="15" cy="15" r="12" fill="none" stroke={colors.neutral.light} strokeWidth="2.5" />
            <path d="M10.5 10.5l9 9M19.5 10.5l-9 9" stroke={colors.neutral.mediumLight} strokeWidth="2.5" strokeLinecap="round" />
          </>
        )}
      </svg>
      {why}
    </MarkLine>
  );
}

/** Blocks side by side: a picture, and the points beside it. */
export const Columns = styled.div<{ $widths?: string }>`
  display: grid;
  grid-template-columns: ${(p) => p.$widths ?? '1fr 1fr'};
  column-gap: 70px;
  align-items: center;
`;

type MapPoint = {
  name: string;
  /** Along the x axis, 0 at the left to 1 at the right. */
  x: number;
  /** Along the y axis, 0 at the bottom to 1 at the top. */
  y: number;
  /** The logo; without one, the name's initials. */
  logo?: React.ReactNode;
  /** What its card says when the logo is clicked. */
  about: React.ReactNode;
  /** Ours, drawn larger and ringed. */
  focus?: boolean;
};

/** Players on a 2D plane with both axes labelled, each a logo that opens
 *  a card when clicked (one at a time; a click elsewhere or Escape closes
 *  it). Still — the export, reduced motion — every card is closed.
 *  `compact` sets many players smaller, each logo an app icon's rounded
 *  square; `notes` are words set on the plane, at its own coordinates. */
export const LogoMap: React.FC<{
  /** The x axis's ends, left then right. */
  x: readonly [React.ReactNode, React.ReactNode];
  /** The y axis's ends, bottom then top. */
  y: readonly [React.ReactNode, React.ReactNode];
  points: readonly MapPoint[];
  width: number;
  height: number;
  compact?: boolean;
  notes?: ReadonlyArray<{ x: number; y: number; content: React.ReactNode }>;
  /** The dashed cross through the middle, quartering the plane. */
  cross?: boolean;
  /** Each point's name under its logo; without, the name is on its card. */
  names?: boolean;
  /** What each axis measures, centred along it. */
  xTitle?: React.ReactNode;
  yTitle?: React.ReactNode;
}> = ({ x, y, points, width, height, compact = false, notes = [], cross = true, names = true, xTitle, yTitle }) => {
  const arrive = useArrive();
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(null);
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open]);
  return (
    <MapBox style={{ width, height }} onClick={() => setOpen(null)}>
      <MapAxisY aria-hidden="true" />
      <MapAxisX aria-hidden="true" />
      {cross && <MapMid aria-hidden="true" />}
      <MapLabelY style={{ bottom: 0 }}>{y[0]}</MapLabelY>
      <MapLabelY style={{ top: 0 }}>{y[1]}</MapLabelY>
      <MapLabelX style={{ left: MAP_INSET }}>{x[0]}</MapLabelX>
      <MapLabelX style={{ right: 0, textAlign: 'right' }}>{x[1]}</MapLabelX>
      {xTitle && <MapTitleX>{xTitle}</MapTitleX>}
      {yTitle && <MapTitleY>{yTitle}</MapTitleY>}
      {notes.map((note, i) => (
        <MapNote
          key={i}
          style={{ left: MAP_INSET + note.x * (width - MAP_INSET - MAP_PAD), top: MAP_PAD + (1 - note.y) * (height - MAP_INSET - MAP_PAD) }}
        >
          {note.content}
        </MapNote>
      ))}
      {points.map((point, i) => {
        const left = MAP_INSET + point.x * (width - MAP_INSET - MAP_PAD);
        const top = MAP_PAD + (1 - point.y) * (height - MAP_INSET - MAP_PAD);
        const isOpen = open === point.name;
        return (
          <MapPointBox
            key={point.name}
            $compact={compact}
            $focus={point.focus ?? false}
            style={{ left, top, zIndex: isOpen ? 3 : 1 }}
            {...arrive(i + 1, { opacity: 0, scale: 0.8 })}
          >
            <MapLogo
              type="button"
              $compact={compact}
              $focus={point.focus ?? false}
              aria-expanded={isOpen}
              aria-label={`${point.name}: about`}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(isOpen ? null : point.name);
              }}
            >
              {point.logo ?? <MapInitials>{initials(point.name)}</MapInitials>}
            </MapLogo>
            {names && (
              <MapName $compact={compact} $focus={point.focus ?? false}>
                {point.name}
              </MapName>
            )}
            {isOpen && (
              <MapCard $side={point.x > 0.55 ? 'left' : 'right'} role="note" onClick={(e) => e.stopPropagation()}>
                <strong>{point.name}</strong>
                {point.about}
              </MapCard>
            )}
          </MapPointBox>
        );
      })}
    </MapBox>
  );
};

/** Room for the axes' labels below and to the left, and a margin beyond the
 *  points at the far ends: where a plane's 0 and 1 fall, for a picture that
 *  places its points by pixel. */
export const MAP_INSET = 70;
export const MAP_PAD = 46;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

/** A funnel, narrowing as it goes down: each stage a figure and what it is. */
export const Funnel: React.FC<{
  stages: ReadonlyArray<{ figure: React.ReactNode; text: React.ReactNode; tone?: 'focus' }>;
}> = ({ stages }) => {
  const arrive = useArrive();
  // From the full width at the top to under three quarters of it.
  const width = (i: number) => 100 - (i * 28) / Math.max(stages.length - 1, 1);
  return (
    <FunnelBox>
      {stages.map((stage, i) => (
        <FunnelStage key={i} $focus={stage.tone === 'focus'} style={{ width: `${width(i)}%` }} {...arrive(i + 1)}>
          <FunnelFigure $focus={stage.tone === 'focus'}>{stage.figure}</FunnelFigure>
          <FunnelText>{stage.text}</FunnelText>
        </FunnelStage>
      ))}
    </FunnelBox>
  );
};

/** A slide made outside this frame (the cover, How we build), wearing its
 *  question like the rest. */
export function withQuestion(Slide: React.ComponentType, question: string): React.FC {
  const WithQuestion: React.FC = () => (
    <>
      <Slide />
      {SHOW_QUESTIONS && <FloatingQuestion>Q: {question}</FloatingQuestion>}
    </>
  );
  WithQuestion.displayName = `withQuestion(${Slide.displayName ?? Slide.name})`;
  return WithQuestion;
}

/** One line to remember, under the content. */
export const Callout: React.FC<{ children: React.ReactNode; order?: number }> = ({ children, order = 5 }) => {
  const arrive = useArrive();
  return <CalloutBox {...arrive(order)}>{children}</CalloutBox>;
};

/** Short quotes, each with where it's from: a Callout whose evidence is
 *  other people's words, led by the line they bear out, or followed by the
 *  line they add up to. */
export const Quotes: React.FC<{
  quotes: ReadonlyArray<{ text: React.ReactNode; source: React.ReactNode }>;
  lead?: React.ReactNode;
  takeaway?: React.ReactNode;
  order?: number;
  /** Column widths, when one quote needs more room than the rest. */
  widths?: string;
}> = ({ quotes, lead, takeaway, order = 5, widths }) => {
  const arrive = useArrive();
  return (
    <CalloutBox {...arrive(order)}>
      {lead && <QuoteLead>{lead}</QuoteLead>}
      <QuoteGrid style={{ gridTemplateColumns: widths ?? `repeat(${quotes.length}, 1fr)` }}>
        {quotes.map((quote, i) => (
          <QuoteFigure key={i}>
            <blockquote>“{quote.text}”</blockquote>
            <figcaption>{quote.source}</figcaption>
          </QuoteFigure>
        ))}
      </QuoteGrid>
      {takeaway && <Takeaway>{takeaway}</Takeaway>}
    </CalloutBox>
  );
};

/** A small source or footnote line. */
export const Footnote = styled.div`
  margin-top: 20px;
  font-family: ${typography.fontFamily.small};
  font-size: 16px;
  color: ${colors.highlight.light};
`;

/** Text in the coral the headings use for emphasis. */
export const Accent = styled.span`
  color: ${colors.accent.coral.extraDark};
`;

/** A draft gap June fills: shown as a marked placeholder, not as fact. */
export const ToFill = styled.span`
  padding: 0 8px;
  border-radius: 6px;
  background: ${colors.accent.yellow.light};
  border: 1px dashed ${colors.accent.yellow.extraDark};
  color: ${colors.neutral.extraDark};
  font-weight: 600;
`;

// ── Styles (canvas pixels, see SlideCanvas) ─────────────────────────────────

const Top = styled(motion.div)`
  position: absolute;
  left: 140px;
  right: 140px;
  top: 64px;
`;

const Eyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.exxxtraDark};
`;

const Question = styled.div`
  display: inline-block;
  margin-top: 12px;
  padding: 5px 14px;
  border-radius: 9999px;
  border: 1px solid ${colors.accent.coral.light};
  background: ${colors.accent.coral.exxtraLight};
  font-family: ${typography.fontFamily.small};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.accent.coral.exxxtraDark};
`;

const Headline = styled.h2<{ $long?: boolean }>`
  margin: 14px 0 0;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: ${(p) => (p.$long ? '40px' : '50px')};
  font-weight: 300;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: ${colors.neutral.extraDark};
  text-wrap: balance;
`;

const Body = styled.div`
  position: absolute;
  left: 140px;
  right: 140px;
  top: 320px;
  bottom: 90px;
  /* Centred under the headline, a little above the middle. */
  padding-bottom: 70px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const Panel = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 760px;
  padding: 72px 70px 72px 110px;
  background: ${colors.brand.exxxxtraDark};
  color: ${colors.white};
  display: flex;
  flex-direction: column;
`;

const PanelEyebrow = styled.div`
  font-family: ${typography.fontFamily.small};
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${colors.brand.extraDark};
`;

const PanelQuestion = styled.div`
  display: inline-block;
  margin-top: 12px;
  padding: 5px 14px;
  border-radius: 9999px;
  background: color-mix(in srgb, ${colors.white} 10%, transparent);
  border: 1px solid color-mix(in srgb, ${colors.white} 25%, transparent);
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 600;
  color: ${colors.accent.coral.light};
`;

const PanelHeadline = styled.h2`
  margin: 16px 0 0;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 48px;
  font-weight: 300;
  line-height: 1.15;
  letter-spacing: -0.015em;
  text-wrap: balance;
`;

const PanelPoints = styled.ul`
  margin: 34px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 16px;

  li {
    position: relative;
    padding-left: 24px;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 23px;
    line-height: 1.4;
    color: color-mix(in srgb, ${colors.white} 88%, transparent);
  }

  li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 12px;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: ${colors.accent.coral.dark};
  }

  strong {
    color: ${colors.white};
    font-weight: 600;
  }
`;

const Picture = styled.div`
  position: absolute;
  left: 820px;
  right: 90px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleBlock = styled(motion.div)`
  position: absolute;
  left: 160px;
  right: 160px;
  top: 50%;
  transform: translateY(-50%);
`;

const BigTitle = styled.h1`
  margin: 0;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 96px;
  font-weight: 300;
  letter-spacing: -0.03em;
  color: ${colors.neutral.extraDark};
`;

const BigSubtitle = styled.div`
  margin-top: 18px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 34px;
  font-weight: 300;
  color: ${colors.neutral.darkMedium};
  text-wrap: balance;
`;

const TitleNote = styled.div`
  margin-top: 28px;
  font-family: ${typography.fontFamily.small};
  font-size: 18px;
  color: ${colors.highlight.light};
`;

const Cards = styled.div`
  display: grid;
  gap: 26px;
`;

const Card = styled(motion.div)<{ $focus: boolean; $compact?: boolean }>`
  padding: ${(p) => (p.$compact ? '22px 26px' : '34px 36px')};
  border-radius: 24px;
  background: ${colors.white};
  box-shadow: 0 14px 28px ${colors.shadowSoft};
  border-top: 5px solid ${(p) => (p.$focus ? colors.accent.coral.extraDark : colors.brand.extraDark)};
`;

const CardFigure = styled.div<{ $compact?: boolean }>`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: ${(p) => (p.$compact ? 44 : 64)}px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: -0.02em;
  color: ${colors.brand.exxxtraDark};
  margin-bottom: ${(p) => (p.$compact ? 10 : 14)}px;
`;

const CardTitle = styled.div<{ $compact?: boolean }>`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${(p) => (p.$compact ? 21 : 28)}px;
  font-weight: 500;
  line-height: 1.25;
  color: ${colors.neutral.extraDark};
`;

const CardText = styled.div<{ $compact?: boolean }>`
  margin-top: ${(p) => (p.$compact ? 8 : 12)}px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${(p) => (p.$compact ? 17 : 22)}px;
  line-height: 1.45;
  color: ${colors.neutral.darkMedium};
  text-wrap: pretty;

  ul {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 5px;
  }
`;

const PointList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 18px;

  li {
    position: relative;
    padding-left: 30px;
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 26px;
    line-height: 1.4;
    color: ${colors.neutral.darkMedium};
  }

  li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 13px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${colors.accent.coral.extraDark};
  }

  strong {
    color: ${colors.neutral.extraDark};
    font-weight: 600;
  }
`;

/* Never shrunk to fit a full slide: clipped to its corners, a shrunk table
   would cut off its last row. */
const TableBox = styled(motion.div)`
  flex-shrink: 0;
  border-radius: 20px;
  overflow: hidden;
  background: ${colors.white};
  box-shadow: 0 14px 28px ${colors.shadowSoft};
`;

const TableRow = styled.div<{ $head?: boolean; $focus?: boolean }>`
  display: grid;
  background: ${(p) => (p.$head ? colors.brand.exxxxtraDark : p.$focus ? colors.accent.coral.exxtraLight : 'transparent')};
  border-top: ${(p) => (p.$head ? 'none' : `1px solid ${colors.neutral.extraLight}`)};
`;

const TableCell = styled.div<{ $head?: boolean; $name?: boolean; $focus?: boolean }>`
  padding: ${(p) => (p.$head ? '18px 26px' : '20px 26px')};
  font-family: ${(p) => (p.$head ? typography.fontFamily.small : `'Poppins', ${typography.fontFamily.body}`)};
  font-size: ${(p) => (p.$head ? '16px' : '21px')};
  font-weight: ${(p) => (p.$head || p.$name ? 600 : 400)};
  letter-spacing: ${(p) => (p.$head ? '0.12em' : '0')};
  text-transform: ${(p) => (p.$head ? 'uppercase' : 'none')};
  line-height: 1.4;
  color: ${(p) =>
    p.$head ? (p.$focus ? colors.accent.coral.light : colors.white) : p.$name ? colors.neutral.extraDark : colors.neutral.darkMedium};
  background: ${(p) => (!p.$head && p.$focus ? colors.accent.coral.exxtraLight : 'transparent')};
`;

const PhaseRow = styled.div`
  position: relative;
  display: grid;
  gap: 28px;

  /* Under another block, its line clear of that block's foot. */
  &:not(:first-child) {
    margin-top: 40px;
  }
`;

const PhaseLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 13px;
  height: 3px;
  background: ${colors.brand.extraDark};
`;

const Phase = styled(motion.div)`
  position: relative;
`;

const PhaseDot = styled.div<{ $now: boolean }>`
  width: 29px;
  height: 29px;
  border-radius: 50%;
  border: 4px solid ${colors.white};
  background: ${(p) => (p.$now ? colors.accent.coral.extraDark : colors.brand.exxtraDark)};
  box-shadow: 0 4px 10px ${colors.shadowSoft};
`;

const PhaseWhen = styled.div<{ $now: boolean }>`
  margin-top: 18px;
  font-family: ${typography.fontFamily.small};
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${(p) => (p.$now ? colors.accent.coral.exxxtraDark : colors.brand.exxxtraDark)};
`;

const PhaseName = styled.div`
  margin-top: 6px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 30px;
  font-weight: 500;
  line-height: 1.25;
  color: ${colors.neutral.extraDark};
`;

const PhaseItems = styled.ul`
  margin: 12px 0 0;
  padding: 0 0 0 20px;

  li {
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 21px;
    line-height: 1.45;
    color: ${colors.neutral.darkMedium};
    margin-bottom: 7px;
  }
`;

/* A phase's callout: a short boxed point under its items. */
const PhaseNote = styled.div`
  margin-top: 6px;
  padding: 12px 16px;
  border-radius: 14px;
  border-left: 5px solid ${colors.accent.coral.extraDark};
  background: ${colors.white};
  box-shadow: 0 8px 18px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 19px;
  line-height: 1.4;
  color: ${colors.neutral.extraDark};
`;

const LoopBox = styled.div`
  position: relative;
`;

const LoopCentre = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  width: 280px;
  transform: translate(-50%, -50%);
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 30px;
  font-weight: 500;
  line-height: 1.25;
  color: ${colors.brand.exxxtraDark};
  text-wrap: balance;
`;

const LoopStep = styled(motion.div)`
  position: absolute;
  width: 250px;
  margin: -50px 0 0 -125px;
  padding: 16px 18px;
  border-radius: 18px;
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 23px;
  font-weight: 500;
  line-height: 1.3;
  color: ${colors.neutral.extraDark};
`;

/* A verdict reads in the table's ink when it is a yes, quieter when not. */
const MarkLine = styled.span<{ $is: MarkKind }>`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => (p.$is === 'yes' ? colors.neutral.extraDark : colors.neutral.medium)};

  svg {
    flex-shrink: 0;
  }
`;

const MapBox = styled.div`
  position: relative;
`;

/* The axes, from the corner by the labels: up to the top, across to the
   right, each ending in an arrowhead. */
const MapAxisY = styled.div`
  position: absolute;
  left: ${MAP_INSET - 22}px;
  top: 0;
  bottom: ${MAP_INSET - 22}px;
  width: 2px;
  background: ${colors.highlight.extraLight};

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -6px;
    border: 7px solid transparent;
    border-bottom: 11px solid ${colors.highlight.extraLight};
    border-top: 0;
  }
`;

const MapAxisX = styled.div`
  position: absolute;
  left: ${MAP_INSET - 22}px;
  right: 0;
  bottom: ${MAP_INSET - 22}px;
  height: 2px;
  background: ${colors.highlight.extraLight};

  &::after {
    content: '';
    position: absolute;
    right: -2px;
    top: -6px;
    border: 7px solid transparent;
    border-left: 11px solid ${colors.highlight.extraLight};
    border-right: 0;
  }
`;

/* The plane's centre lines, faint, so the four corners read as regions. */
const MapMid = styled.div`
  position: absolute;
  left: ${MAP_INSET}px;
  right: ${MAP_PAD}px;
  top: ${MAP_PAD}px;
  bottom: ${MAP_INSET}px;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
  }

  &::before {
    left: 50%;
    top: 0;
    bottom: 0;
    border-left: 2px dashed ${colors.neutral.light};
  }

  &::after {
    top: 50%;
    left: 0;
    right: 0;
    border-top: 2px dashed ${colors.neutral.light};
  }
`;

const MapLabel = styled.div`
  position: absolute;
  font-family: ${typography.fontFamily.small};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${colors.brand.exxxtraDark};
`;

/* Read bottom to top, beside the y axis. */
const MapLabelY = styled(MapLabel)`
  left: 0;
  width: ${MAP_INSET - 34}px;
  writing-mode: vertical-rl;
  transform: rotate(180deg);
`;

const MapLabelX = styled(MapLabel)`
  bottom: 0;
  height: ${MAP_INSET - 36}px;
  display: flex;
  align-items: flex-end;
`;

/* An axis's title, centred along it, between its ends' labels. */
const MapTitleX = styled(MapLabelX)`
  left: ${MAP_INSET}px;
  right: ${MAP_PAD}px;
  justify-content: center;
`;

const MapTitleY = styled(MapLabelY)`
  top: 0;
  bottom: ${MAP_INSET}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

/* A point: the logo centred on its spot, the name under it. */
const MapPointBox = styled(motion.div)<{ $compact: boolean; $focus: boolean }>`
  position: absolute;
  width: ${(p) => (p.$compact ? 112 : 150)}px;
  margin-left: ${(p) => (p.$compact ? -56 : -75)}px;
  margin-top: ${(p) => (p.$compact ? (p.$focus ? -25 : -17) : -34)}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Only the logo and its card take a click: the box is wider than a
     compact logo, and would catch the clicks meant for its neighbours. */
  pointer-events: none;

  > button,
  > [role='note'] {
    pointer-events: auto;
  }
`;

/* Compact, the logo is the app's icon, filling a rounded square as it
   does on a phone. */
const MapLogo = styled.button<{ $focus: boolean; $compact: boolean }>`
  width: ${(p) => (p.$compact ? (p.$focus ? 50 : 34) : p.$focus ? 88 : 68)}px;
  height: ${(p) => (p.$compact ? (p.$focus ? 50 : 34) : p.$focus ? 88 : 68)}px;
  margin-top: ${(p) => (p.$focus && !p.$compact ? -10 : 0)}px;
  padding: ${(p) => (p.$compact ? 0 : p.$focus ? 14 : 12)}px;
  border-radius: 50%;
  border: ${(p) =>
    p.$focus ? `3px solid ${colors.accent.coral.exxxtraDark}` : `${p.$compact ? 1 : 2}px solid ${colors.neutral.extraLight}`};
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;

  img,
  svg {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  /* Logos come as bare marks or as square tiles; rounded like app icons,
     the tiles read as one set with the marks. */
  img {
    border-radius: ${(p) => (p.$compact ? 0 : '22%')};
  }

  &:focus-visible {
    outline: 3px solid ${colors.brand.exxtraDark};
    outline-offset: 3px;
  }
`;

const MapInitials = styled.span`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 22px;
  font-weight: 600;
  color: ${colors.brand.exxxtraDark};
`;

const MapName = styled.div<{ $focus: boolean; $compact: boolean }>`
  margin-top: ${(p) => (p.$compact ? 3 : 6)}px;
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: ${(p) => (p.$compact ? (p.$focus ? 15 : 13) : p.$focus ? 18 : 15)}px;
  font-weight: ${(p) => (p.$focus ? 600 : 500)};
  white-space: nowrap;
  color: ${(p) => (p.$focus ? colors.accent.coral.exxxtraDark : colors.neutral.extraDark)};
`;

/* The card a click opens, beside the logo, on whichever side has room. */
/* Beside the point's whole box, so it clears the name under a logo as well
   as the logo: a name can be wider than its logo. */
const MapCard = styled.div<{ $side: 'left' | 'right' }>`
  position: absolute;
  top: -8px;
  ${(p) => (p.$side === 'right' ? 'left: calc(100% + 4px);' : 'right: calc(100% + 4px);')}
  width: 320px;
  padding: 14px 18px;
  border-radius: 16px;
  background: ${colors.white};
  box-shadow: 0 14px 30px ${colors.shadowHard};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 16px;
  line-height: 1.4;
  color: ${colors.neutral.darkMedium};
  text-align: left;

  strong {
    display: block;
    margin-bottom: 4px;
    font-size: 18px;
    font-weight: 600;
    color: ${colors.neutral.extraDark};
  }

  p {
    margin: 6px 0 0;
  }

  b {
    font-weight: 600;
    color: ${colors.neutral.extraDark};
  }

  a {
    color: ${colors.brand.exxxtraDark};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

/* Words set on the plane, centred on their point. */
const MapNote = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const FunnelBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const FunnelStage = styled(motion.div)<{ $focus: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  column-gap: 20px;
  padding: 18px 26px;
  border-radius: 18px;
  border-left: 6px solid ${(p) => (p.$focus ? colors.accent.coral.extraDark : colors.brand.extraDark)};
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
`;

const FunnelFigure = styled.div<{ $focus: boolean }>`
  font-family: 'Poppins', ${typography.fontFamily.heading};
  font-size: 36px;
  font-weight: 600;
  line-height: 1.1;
  letter-spacing: -0.02em;
  color: ${(p) => (p.$focus ? colors.accent.coral.exxxtraDark : colors.brand.exxxtraDark)};
`;

const FunnelText = styled.div`
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 20px;
  line-height: 1.35;
  color: ${colors.neutral.darkMedium};
`;

/* The question on a slide made outside the frame: top centre, clear of
   the viewer's title (top left) and menu (top right). Viewport pixels. */
const FloatingQuestion = styled.div`
  position: absolute;
  left: 50%;
  top: 14px;
  transform: translateX(-50%);
  z-index: 15;
  padding: 4px 12px;
  border-radius: 9999px;
  border: 1px solid ${colors.accent.coral.light};
  background: ${colors.accent.coral.exxtraLight};
  font-family: ${typography.fontFamily.small};
  font-size: 13px;
  font-weight: 600;
  color: ${colors.accent.coral.exxxtraDark};
  white-space: nowrap;
  pointer-events: none;
`;

const CalloutBox = styled(motion.div)`
  margin-top: 30px;
  padding: 20px 28px;
  border-radius: 18px;
  border-left: 6px solid ${colors.accent.coral.extraDark};
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};
  font-size: 26px;
  line-height: 1.4;
  color: ${colors.neutral.extraDark};
`;

const QuoteLead = styled.div`
  margin-bottom: 12px;
  font-size: 25px;
  line-height: 1.35;
  color: ${colors.accent.coral.extraDark};
`;

const QuoteGrid = styled.div`
  display: grid;
  column-gap: 40px;
`;

const QuoteFigure = styled.figure`
  margin: 0;

  blockquote {
    margin: 0;
    font-size: 23px;
    line-height: 1.35;
    color: ${colors.neutral.extraDark};
  }

  figcaption {
    margin-top: 6px;
    font-family: ${typography.fontFamily.small};
    font-size: 15px;
    color: ${colors.highlight.light};
  }
`;

const Takeaway = styled.div`
  margin-top: 14px;
  font-size: 25px;
  line-height: 1.35;
  color: ${colors.accent.coral.extraDark};
`;
